/**
 * #311: orphan order 재시도 가드 (옵션 A — sessionStorage).
 *
 * paymentApi 실패 시 order_id 를 sessionStorage 에 기록 → 동일 cart 재시도 시
 * 기존 order 재사용 (createOrder 미호출) → orphan 누적 차단.
 *
 * 보안: order_id 의 ownership / 상태 검증은 admin server-side 에 의존.
 * sessionStorage 는 사용자 조작 가능하나 admin 이 403/422 차단.
 *
 * fail-open (Codex P1): sessionStorage read/write/JSON parse 만 좁게 try/catch —
 * silent degrade 회피 (#1196 패턴). 실패 시 기능 무시 (기존 동작 유지).
 * 사용자/주문/결제 식별자 원문 로깅 금지.
 */
import type { CartItem } from '@/components/CartModal';
import { extractMenuId } from '@/utils/cartUtils';

const STORAGE_KEY = 'olulo_orphan_order';
const TTL_MS = 30 * 60 * 1000; // 30분

/**
 * orphan record 가 박제하는 주문유형 (BE `order_type` 과 동일 도메인: pickup | table).
 * #1705 R7 (Codex P1): order_type 별로 결제 조합 scope 가 갈리므로 재사용 시 반드시 일치 검증.
 */
export type OrphanOrderType = 'pickup' | 'table';

export interface OrphanOrderRecord {
  order_id: number;
  /** Last known server total, including tip. Old records may omit it. */
  total?: number;
  store_id: number;
  cart_signature: string;
  /** rev 4 hot-fix (Codex P2): 재사용 경로의 createPaymentData 에서 currency 사용 — undefined 회피 */
  currency: string;
  /**
   * #1705 R7 (Codex P1): 주문 생성 시점의 주문유형. order_type 별 결제 조합 scope 차등으로
   * pickup orphan 을 table 체크아웃에서 재사용하면 table-scope 조합이 pickup 주문에 붙어
   * FE 정합이 깨진다. 매칭 시 현재 checkout orderType 과 일치해야만 재사용.
   */
  order_type: OrphanOrderType;
  /**
   * #1705 R7 (Codex P1): 테이블 오더 식별자. table 주문은 동일 매장이라도 테이블이 다르면
   * 별개 주문이므로 함께 매칭. pickup 주문은 undefined.
   */
  table_number?: string;
  expires_at: number;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof sessionStorage !== 'undefined';
}

/**
 * cart_signature 계산 — items 메뉴 구성 식별 (rev 3).
 *
 * 포함: menu_id (extractMenuId 동일 규칙) / quantity / options 정렬 직렬화 / customizations
 * 제외: total / tip* / pointsToUse / customer_* / notes — 사용자 재선택 가능 또는 BE 검증 영역
 */
export function computeCartSignature(items: CartItem[]): string {
  const normalized = items
    .map((item) => ({
      menu_id: extractMenuId(item.id),
      quantity: item.quantity,
      options: serializeOptions(item.selectedOptions),
      customizations: [...(item.customizations ?? [])].sort().join('|'),
    }))
    .sort((a, b) => a.menu_id.localeCompare(b.menu_id));

  return JSON.stringify(normalized);
}

function serializeOptions(options: Record<string, string[]> | undefined): string {
  if (!options) {
    return '';
  }
  const sortedKeys = Object.keys(options).sort();
  return sortedKeys
    .map((key) => {
      const sortedValues = [...(options[key] ?? [])].sort();
      return `${key}:${sortedValues.join(',')}`;
    })
    .join(';');
}

/**
 * 동일 store + cart_signature + **order_type(+ table_number)** 매칭 + 미만료 record 만 반환.
 * 그 외 null (불일치 record 는 즉시 삭제 → 호출자가 새 주문 생성).
 *
 * #1705 R7 (Codex P1): order_type 별 결제 조합 scope 차등으로, pickup orphan 을 table
 * 체크아웃에서 재사용하면 정합이 깨진다. order_type 과 (table 인 경우) table_number 까지
 * 일치할 때만 재사용. 불일치 시 record 삭제하여 새 주문 생성 경로로 폴백.
 */
export function getOrphanOrder(
  storeId: number,
  cartSignature: string,
  orderType: OrphanOrderType,
  tableNumber?: string,
): OrphanOrderRecord | null {
  if (!isBrowser()) {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const record = JSON.parse(raw) as OrphanOrderRecord;
    if (
      record.store_id !== storeId ||
      record.cart_signature !== cartSignature ||
      record.order_type !== orderType ||
      // table 오더는 테이블 식별자까지 일치해야 동일 주문. pickup 은 양쪽 undefined.
      (record.table_number ?? undefined) !== (tableNumber ?? undefined) ||
      record.expires_at <= Date.now()
    ) {
      // 다른 store / 다른 cart / 다른 주문유형·테이블 / 만료 → 즉시 삭제 (E3, E2)
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return record;
  } catch {
    return null;
  }
}

export function saveOrphanOrder(
  record: Omit<OrphanOrderRecord, 'expires_at'>,
): void {
  if (!isBrowser()) {
    return;
  }
  try {
    const full: OrphanOrderRecord = {
      ...record,
      expires_at: Date.now() + TTL_MS,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(full));
  } catch {
    // fail-open
  }
}

export function clearOrphanOrder(): void {
  if (!isBrowser()) {
    return;
  }
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // fail-open
  }
}
